/**
 * Admin — Bulk Kit Settings.
 * Route: /admin/settings/bulk-kit
 *
 * Feb 2026 (SCWT-BULK-BOX-DIMS) — configurable pricing + per-box
 * parcel dimensions for the bulk sample kit workflow. Passed to
 * Starshipit as L × W × H × weight on every outbound label.
 */
import { useEffect, useMemo, useState } from "react";
import { Save, RotateCcw, Package, DollarSign, Ruler } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import api from "@/lib/api";
import { toast } from "sonner";

const FIELDS = [
  { key: "partner_kit_price_cents",           label: "Partner pays per kit",           unit: "$", group: "pricing" },
  { key: "customer_partner_kit_charge_cents", label: "Customer pays (partner supplied)", unit: "$", group: "pricing" },
  { key: "partner_kit_reimbursement_cents",   label: "Partner reimbursement per kit",   unit: "$", group: "pricing" },
  { key: "aware_distribution_fee_cents",      label: "AWARE distribution fee per kit",  unit: "$", group: "pricing" },
  { key: "kits_per_box",                      label: "Kits per box",                    unit: "count", group: "packaging" },
  { key: "box_length_mm",                     label: "Box length",                      unit: "mm", group: "parcel" },
  { key: "box_width_mm",                      label: "Box width",                       unit: "mm", group: "parcel" },
  { key: "box_height_mm",                     label: "Box height",                      unit: "mm", group: "parcel" },
  { key: "box_weight_g",                      label: "Box weight (packed)",             unit: "g",  group: "parcel" },
];

export default function AdminBulkKitSettings() {
  const [state, setState]     = useState(null);
  const [defaults, setDefs]   = useState(null);
  const [draft, setDraft]     = useState({});
  const [saving, setSaving]   = useState(false);
  const [warnings, setWarns]  = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/settings/bulk-kit").then(({ data }) => {
      setState(data.settings);
      setDefs(data.defaults);
      setDraft(data.settings);
      setWarns(data.warnings || []);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const dirty = useMemo(() => state && Object.keys(draft).some((k) => draft[k] !== state[k]), [state, draft]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = Object.fromEntries(
        Object.entries(draft).filter(([k]) => FIELDS.some((f) => f.key === k))
      );
      const { data } = await api.put("/admin/settings/bulk-kit", patch);
      setState(data.settings); setDraft(data.settings); setWarns(data.warnings || []);
      toast.success("Bulk kit settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const resetToDefaults = () => setDraft({ ...defaults });

  const displayValue = (f, val) => {
    if (val == null) return "";
    if (f.unit === "$") return (val / 100).toFixed(2);
    return String(val);
  };
  const parseValue = (f, str) => {
    if (str === "") return null;
    if (f.unit === "$") return Math.round(parseFloat(str) * 100);
    return parseInt(str, 10);
  };

  const dims = draft && [
    draft.box_length_mm ? (draft.box_length_mm / 10).toFixed(1) : "?",
    draft.box_width_mm  ? (draft.box_width_mm  / 10).toFixed(1) : "?",
    draft.box_height_mm ? (draft.box_height_mm / 10).toFixed(1) : "?",
  ].join(" × ") + " cm";
  const kg = draft?.box_weight_g ? (draft.box_weight_g / 1000).toFixed(2) + " kg" : "";

  return (
    <PortalShell title="Bulk Kit Settings" subtitle="Pricing + per-box parcel dimensions for bulk partner orders">
      <div className="max-w-3xl mx-auto" data-testid="bulk-kit-settings-page">
        {loading && <div className="text-slate-500 text-sm">Loading…</div>}
        {!loading && state && (
          <div className="space-y-6">
            {warnings.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-1">
                {warnings.map((w, i) => <div key={i}>⚠️ {w}</div>)}
              </div>
            )}

            {/* Pricing group */}
            <Section icon={DollarSign} title="Pricing (per kit)">
              {FIELDS.filter((f) => f.group === "pricing").map((f) => (
                <Field key={f.key} field={f} draft={draft} setDraft={setDraft} defaults={defaults}
                       displayValue={displayValue} parseValue={parseValue} />
              ))}
            </Section>

            {/* Packaging group */}
            <Section icon={Package} title="Packaging">
              {FIELDS.filter((f) => f.group === "packaging").map((f) => (
                <Field key={f.key} field={f} draft={draft} setDraft={setDraft} defaults={defaults}
                       displayValue={displayValue} parseValue={parseValue} />
              ))}
            </Section>

            {/* Parcel dimensions group */}
            <Section icon={Ruler} title="Bulk box parcel dimensions"
                     subtitle={`Passed to Starshipit → AusPost as ${dims} · ${kg}. Drives cubic-weight billing per box.`}>
              {FIELDS.filter((f) => f.group === "parcel").map((f) => (
                <Field key={f.key} field={f} draft={draft} setDraft={setDraft} defaults={defaults}
                       displayValue={displayValue} parseValue={parseValue} />
              ))}
            </Section>

            <div className="flex items-center gap-3 pt-4 border-t">
              <button
                type="button"
                data-testid="bulk-kit-settings-save"
                onClick={save}
                disabled={!dirty || saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save settings"}
              </button>
              <button
                type="button"
                data-testid="bulk-kit-settings-reset"
                onClick={resetToDefaults}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
              >
                <RotateCcw className="w-4 h-4" /> Reset to defaults
              </button>
            </div>
          </div>
        )}
      </div>
    </PortalShell>
  );
}

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-cyan-700" />
        <h2 className="text-sm font-bold text-slate-900">{title}</h2>
      </div>
      {subtitle && <p className="text-xs text-slate-500 mb-4 -mt-2">{subtitle}</p>}
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ field, draft, setDraft, defaults, displayValue, parseValue }) {
  const val = displayValue(field, draft[field.key]);
  const def = defaults?.[field.key];
  return (
    <div className="grid grid-cols-3 gap-3 items-center">
      <label className="text-xs text-slate-600 col-span-2">
        {field.label}
        <div className="text-[10px] text-slate-400 mt-0.5">
          default: {field.unit === "$" ? `$${(def / 100).toFixed(2)}` : `${def} ${field.unit}`}
        </div>
      </label>
      <div className="relative">
        {field.unit === "$" && (
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
        )}
        <input
          type="number"
          step={field.unit === "$" ? "0.01" : "1"}
          value={val}
          data-testid={`bulk-kit-settings-input-${field.key}`}
          onChange={(e) => setDraft((d) => ({ ...d, [field.key]: parseValue(field, e.target.value) }))}
          className={`w-full ${field.unit === "$" ? "pl-6" : "pl-3"} pr-16 py-2 rounded-lg border border-slate-300 text-sm font-mono focus:outline-none focus:border-cyan-500`}
        />
        {field.unit !== "$" && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[10px] uppercase tracking-wider">
            {field.unit}
          </span>
        )}
      </div>
    </div>
  );
}
